import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './NumberBlast.css';

// Contract ABIs
const NUMBERBLAST_ABI = [
  "function playOddEven(uint8 choice, uint256 betAmount) external",
  "function playHighLow(uint8 choice, uint256 betAmount) external", 
  "function playExactNumber(uint8 exactNumber, uint256 betAmount) external",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8 betType, uint8 exactNumber, uint8 drawnNumber, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won)[])",
  "function getGameConfig() external view returns (tuple(uint256 houseWinChance, uint256 oddChance, uint256 evenChance, uint256 highChance, uint256 lowChance, uint256 exactChance, uint256 oddEvenPayout, uint256 highLowPayout, uint256 exactPayout))",
  "function calculateHouseEdge(uint8 betType) external view returns (uint256)",
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function getBetTypeInfo(uint8 betType) external view returns (string name, uint256 payout)",
  "function owner() external view returns (address)",
  "function admin() external view returns (address)", 
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function fundHouse(uint256 amount) external",
  "function withdrawHouse(uint256 amount) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function updateWinChances(uint256 houseWin, uint256 odd, uint256 even, uint256 high, uint256 low, uint256 exact) external",
  "function updatePayouts(uint256 oddEvenPayout, uint256 highLowPayout, uint256 exactPayout) external",
  "event GamePlayed(address indexed player, uint256 betAmount, uint8 betType, uint8 exactNumber, uint8 drawnNumber, uint256 grossPayout, uint256 netPayout, bool won)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  NUMBERBLAST: "0x489692449D7d76a840717780c0409186dC37c8D4", // Replace with actual address
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"
};

// Bet types configuration
const BET_TYPES = [
  { id: 0, name: 'ODD', icon: '⚡', description: 'Numbers 1,3,5...99', color: '#00ff41', glowColor: '0, 255, 65' },
  { id: 1, name: 'EVEN', icon: '🔋', description: 'Numbers 2,4,6...100', color: '#00d4ff', glowColor: '0, 212, 255' },
  { id: 2, name: 'HIGH', icon: '⬆️', description: 'Numbers 51-100', color: '#ff6b00', glowColor: '255, 107, 0' },
  { id: 3, name: 'LOW', icon: '⬇️', description: 'Numbers 1-50', color: '#ff0080', glowColor: '255, 0, 128' },
  { id: 4, name: 'EXACT', icon: '🎯', description: 'Pick exact number', color: '#ffd700', glowColor: '255, 215, 0' }
];

// 🎵 CYBER SOUND SYSTEM 🎵
const playCyberSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 50);
      });
    };

    switch (type) {
      case 'codeBreaching':
        createComplexSound([
          { freq: 440, duration: 0.1, wave: 'square', delay: 0 },
          { freq: 550, duration: 0.1, wave: 'sawtooth', delay: 50 },
          { freq: 660, duration: 0.15, wave: 'triangle', delay: 100 },
          { freq: 880, duration: 0.2, wave: 'sine', delay: 150 }
        ], 0.15);
        break;
        
      case 'systemBreach':
        createComplexSound([
          { freq: 200, duration: 0.3, wave: 'sawtooth', delay: 0 },
          { freq: 400, duration: 0.3, wave: 'square', delay: 100 },
          { freq: 600, duration: 0.4, wave: 'triangle', delay: 200 },
          { freq: 800, duration: 0.5, wave: 'sine', delay: 300 }
        ], 0.25);
        break;
        
      case 'matrixWin':
        createComplexSound([
          { freq: 659, duration: 0.3, wave: 'triangle', delay: 0 },
          { freq: 784, duration: 0.3, wave: 'triangle', delay: 100 },
          { freq: 988, duration: 0.3, wave: 'triangle', delay: 200 },
          { freq: 1319, duration: 0.5, wave: 'sine', delay: 300 }
        ], 0.3);
        break;
        
      case 'systemFailure':
        createComplexSound([
          { freq: 800, duration: 0.2, wave: 'sawtooth', delay: 0 },
          { freq: 600, duration: 0.2, wave: 'square', delay: 100 },
          { freq: 400, duration: 0.3, wave: 'triangle', delay: 200 },
          { freq: 200, duration: 0.4, wave: 'sawtooth', delay: 300 }
        ], 0.2);
        break;
        
      case 'digitalClick':
        createComplexSound([
          { freq: 1200, duration: 0.05, wave: 'square', delay: 0 },
          { freq: 1400, duration: 0.05, wave: 'triangle', delay: 20 }
        ], 0.05);
        break;
        
      case 'matrixHover':
        createTone(1000, 0.08, 'triangle');
        break;
        
      default:
        createTone(800, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const NumberBlast = ({ walletData, onBack }) => {
  const [gameState, setGameState] = useState({
    betAmount: '1',
    selectedBetType: null,
    exactNumber: '50',
    isPlaying: false,
    isApproving: false,
    lastResult: null,
    balance: '0',
    allowance: '0',
    playerLevel: 1,
    experience: 0,
    streak: 0,
    showBreachPopup: false
  });

  const [gameStats, setGameStats] = useState({
    totalGames: 0,
    totalBets: '0',
    totalPayouts: '0',
    houseEdge: '5',
    effectiveMultiplier: '1.95',
    playersOnline: 0
  });

  const [gameHistory, setGameHistory] = useState([]);
  const [animationState, setAnimationState] = useState('idle');
  const [breachResult, setBreachResult] = useState(null);
  const [matrixNumbers, setMatrixNumbers] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    numberBlast: null,
    blbToken: null
  });
  
  // Admin Panel State
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminState, setAdminState] = useState({
    houseBalance: '0',
    isPaused: false,
    fundAmount: '',
    withdrawAmount: '',
    minBet: '1',
    maxBet: '1000000',
    newWinChances: {
      houseWin: '4.0',
      odd: '15.0',
      even: '15.0', 
      high: '15.0',
      low: '15.0',
      exact: '0.0'
    },
    newPayouts: {
      oddEven: '195',
      highLow: '195',
      exact: '9500'
    }
  });

  const matrixRef = useRef(null);

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playCyberSound(type, volume);
    }
  };

  // Matrix rain effect
  useEffect(() => {
    const generateMatrixNumbers = () => {
      const numbers = [];
      for (let i = 0; i < 50; i++) {
        numbers.push({
          id: i,
          value: Math.floor(Math.random() * 100) + 1,
          x: Math.random() * 100,
          y: Math.random() * 100,
          speed: Math.random() * 2 + 1,
          opacity: Math.random() * 0.7 + 0.3
        });
      }
      setMatrixNumbers(numbers);
    };

    generateMatrixNumbers();
    
    const interval = setInterval(() => {
      setMatrixNumbers(prev => prev.map(num => ({
        ...num,
        y: (num.y + num.speed) % 110,
        value: Math.random() < 0.1 ? Math.floor(Math.random() * 100) + 1 : num.value
      })));
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Initialize contracts
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ numberBlast: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const numberBlastContract = new ethers.Contract(
        CONTRACT_ADDRESSES.NUMBERBLAST,
        NUMBERBLAST_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        numberBlast: numberBlastContract,
        blbToken: blbTokenContract
      });

      setTimeout(() => {
        loadGameData(numberBlastContract);
        loadBalance(blbTokenContract, numberBlastContract);
        checkAdminAccess(numberBlastContract);
      }, 500);

    } catch (error) {
      console.log('Error initializing contracts:', error);
      setContracts({ numberBlast: null, blbToken: null });
    }
  };

  // Check admin access
  const checkAdminAccess = async (contract = contracts.numberBlast) => {
    if (!contract || !walletData?.account) return;

    try {
      const owner = await contract.owner();
      const userIsOwner = owner.toLowerCase() === walletData.account.toLowerCase();
      
      const admin = await contract.admin();
      const userIsAdmin = admin.toLowerCase() === walletData.account.toLowerCase();

      setIsOwner(userIsOwner);
      setIsAdmin(userIsAdmin);

      if (userIsOwner || userIsAdmin) {
        await loadAdminData(contract);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsOwner(false);
      setIsAdmin(false);
    }
  };

  // Load admin data
  const loadAdminData = async (contract = contracts.numberBlast) => {
    if (!contract) return;

    try {
      const [houseBalance, isPaused, betLimits] = await Promise.all([
        contract.houseBalance(),
        contract.paused(),
        Promise.all([contract.minBet(), contract.maxBet()])
      ]);

      setAdminState(prev => ({
        ...prev,
        houseBalance: ethers.utils.formatEther(houseBalance),
        isPaused,
        minBet: ethers.utils.formatEther(betLimits[0]),
        maxBet: ethers.utils.formatEther(betLimits[1])
      }));
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Load game data from contract
  const loadGameData = async (contract = contracts.numberBlast) => {
    if (!contract) return;

    try {
      const [stats, recentGames, config] = await Promise.all([
        contract.getGameStats(),
        contract.getRecentGames(5).catch(() => []),
        contract.getGameConfig()
      ]);

      setGameStats({
        totalGames: parseInt(stats.totalGames.toString()),
        totalBets: ethers.utils.formatEther(stats.totalBets),
        totalPayouts: ethers.utils.formatEther(stats.totalPayoutsAmount),
        houseEdge: '5', // Calculate from config if needed
        effectiveMultiplier: (parseInt(config.oddEvenPayout.toString()) / 100).toString(),
        playersOnline: Math.floor(Math.random() * 300) + 50
      });

      if (recentGames && recentGames.length > 0) {
        const formattedHistory = recentGames.slice(0, 5).map(game => ({
          betType: parseInt(game.betType),
          exactNumber: parseInt(game.exactNumber),
          drawnNumber: parseInt(game.drawnNumber),
          won: game.won,
          betAmount: ethers.utils.formatEther(game.betAmount),
          netPayout: ethers.utils.formatEther(game.netPayout),
          timestamp: parseInt(game.timestamp) * 1000
        }));
        setGameHistory(formattedHistory);
      }
    } catch (error) {
      console.error('Error loading game data:', error);
    }
  };

  // Load BLB balance
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.numberBlast) => {
    if (!tokenContract || !gameContract || !walletData?.account) return;

    try {
      const [balance, allowance] = await Promise.all([
        tokenContract.balanceOf(walletData.account),
        tokenContract.allowance(walletData.account, gameContract.address)
      ]);

      setGameState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  // Handle game play
  const handleBreachCode = async () => {
    if (gameState.selectedBetType === null) {
      setErrorMessage('Select a breach vector first');
      return;
    }

    if (gameState.selectedBetType === 4 && (gameState.exactNumber < 1 || gameState.exactNumber > 100)) {
      setErrorMessage('Target number must be between 1-100');
      return;
    }

    if (!contracts.numberBlast || !contracts.blbToken) {
      setErrorMessage('System connections not established. Reconnect and try again.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');

      const betAmount = parseFloat(gameState.betAmount);
      const balance = parseFloat(gameState.balance);

      if (betAmount <= 0) {
        setErrorMessage('Breach amount must be greater than 0');
        return;
      }

      if (betAmount > balance) {
        setErrorMessage('Insufficient system tokens');
        return;
      }

      playGameSound('codeBreaching', 0.4);
      
      setGameState(prev => ({ 
        ...prev, 
        isPlaying: true, 
        showBreachPopup: true 
      }));
      setAnimationState('breaching');

      const betAmountWei = ethers.utils.parseEther(gameState.betAmount);
      let tx;

      if (gameState.selectedBetType === 0 || gameState.selectedBetType === 1) {
        tx = await contracts.numberBlast.playOddEven(gameState.selectedBetType, betAmountWei);
      } else if (gameState.selectedBetType === 2 || gameState.selectedBetType === 3) {
        tx = await contracts.numberBlast.playHighLow(gameState.selectedBetType, betAmountWei);
      } else if (gameState.selectedBetType === 4) {
        tx = await contracts.numberBlast.playExactNumber(parseInt(gameState.exactNumber), betAmountWei);
      }
      
      setSuccessMessage('Breach initiated! Breaking the code...');
      
      const receipt = await tx.wait();
      
      setTimeout(() => {
        playGameSound('systemBreach', 0.4);
      }, 2000);
      
      const gamePlayedEvent = receipt.events?.find(event => event.event === 'GamePlayed');
      
      if (gamePlayedEvent) {
        const [player, betAmount, betType, exactNumber, drawnNumber, grossPayout, netPayout, won] = gamePlayedEvent.args;
        
        const gameResult = {
          player,
          betAmount: ethers.utils.formatEther(betAmount),
          betType: parseInt(betType),
          exactNumber: parseInt(exactNumber),
          drawnNumber: parseInt(drawnNumber),
          grossPayout: ethers.utils.formatEther(grossPayout),
          netPayout: ethers.utils.formatEther(netPayout),
          won,
          timestamp: Date.now()
        };

        setTimeout(() => {
          handleGameResult(gameResult);
        }, 2500);
        
        await Promise.all([loadGameData(), loadBalance()]);
      } else {
        throw new Error('Game event not found in transaction receipt');
      }

    } catch (error) {
      console.error('Breach error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Breach cancelled by user');
      } else if (error.code === -32603) {
        setErrorMessage('Breach failed. Check your balance and allowance.');
      } else if (error.message.includes('insufficient funds')) {
        setErrorMessage('Insufficient gas or token balance');
      } else if (error.message.includes('allowance')) {
        setErrorMessage('Please approve token access first');
      } else {
        setErrorMessage(`System breach failed: ${error.reason || error.message}`);
      }
      
      setAnimationState('idle');
      setGameState(prev => ({ 
        ...prev, 
        isPlaying: false, 
        showBreachPopup: false 
      }));
      setSuccessMessage('');
    }
  };

  const handleGameResult = (gameResult) => {
    setBreachResult(gameResult);
    setAnimationState('result');

    setTimeout(() => {
      if (gameResult.won) {
        playGameSound('matrixWin', 0.6);
        setSuccessMessage(`🚀 Code breached! +${formatNumber(gameResult.netPayout)} BLB`);
      } else {
        playGameSound('systemFailure', 0.4);
        setErrorMessage(`⚡ System defended: -${formatNumber(gameResult.betAmount)} BLB`);
      }
    }, 500);

    setGameState(prev => ({
      ...prev,
      isPlaying: false,
      streak: gameResult.won ? prev.streak + 1 : 0,
      experience: prev.experience + (gameResult.won ? 100 : 25),
      playerLevel: Math.floor((prev.experience + (gameResult.won ? 100 : 25)) / 200) + 1
    }));

    setTimeout(() => {
      setAnimationState('idle');
      setBreachResult(null);
      setGameState(prev => ({ 
        ...prev, 
        selectedBetType: null,
        isPlaying: false,
        showBreachPopup: false
      }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
  };

  // Handle token approval
  const handleApprove = async () => {
    if (!contracts.blbToken || !contracts.numberBlast) {
      setErrorMessage('System connections not established. Reconnect and try again.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');
      playGameSound('digitalClick', 0.3);
      setGameState(prev => ({ ...prev, isApproving: true }));

      const maxAmount = ethers.constants.MaxUint256;
      const tx = await contracts.blbToken.approve(contracts.numberBlast.address, maxAmount);
      
      setSuccessMessage('Access authorization initiated...');
      await tx.wait();
      
      await loadBalance();
      setSuccessMessage('✅ System access granted!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Authorization denied by user');
      } else {
        setErrorMessage(`Access denied: ${error.reason || error.message}`);
      }
    } finally {
      setGameState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const handleBetTypeSelect = (betTypeId) => {
    playGameSound('digitalClick', 0.3);
    setGameState(prev => ({ ...prev, selectedBetType: betTypeId }));
  };

  const needsApproval = () => {
    return parseFloat(gameState.allowance) < parseFloat(gameState.betAmount);
  };

  const canBreachCode = () => {
    return connected && 
           contracts.numberBlast &&
           contracts.blbToken &&
           gameState.selectedBetType !== null && 
           parseFloat(gameState.balance) >= parseFloat(gameState.betAmount) &&
           parseFloat(gameState.betAmount) > 0 &&
           !needsApproval() &&
           !gameState.isPlaying;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(2);
  };

  const getBetTypeName = (betType) => {
    return BET_TYPES.find(type => type.id === betType)?.name || 'UNKNOWN';
  };

  const getMultiplier = () => {
    if (!gameState.selectedBetType) return '0';
    const betType = BET_TYPES.find(type => type.id === gameState.selectedBetType);
    if (gameState.selectedBetType < 2) return gameStats.effectiveMultiplier;
    if (gameState.selectedBetType < 4) return gameStats.effectiveMultiplier;
    return '95.00'; // Exact number multiplier
  };

  // Connection state
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  if (!connected) {
    return (
      <div className="numberblast-matrix-arena">
        <div className="matrix-rain">
          {matrixNumbers.map(num => (
            <div
              key={num.id}
              className="matrix-number"
              style={{
                left: `${num.x}%`,
                top: `${num.y}%`,
                opacity: num.opacity,
                animationDelay: `${num.id * 0.1}s`
              }}
            >
              {num.value}
            </div>
          ))}
        </div>
        <div className="matrix-connect-screen">
          <div className="connect-icon">🔐</div>
          <h3 className="connect-title">🚀 ACCESS DENIED</h3>
          <p className="connect-desc">Connect your neural interface to access the NumberBlast Matrix system</p>
        </div>
      </div>
    );
  }

  if (!contracts.numberBlast || !contracts.blbToken) {
    return (
      <div className="numberblast-matrix-arena">
        <div className="matrix-rain">
          {matrixNumbers.map(num => (
            <div
              key={num.id}
              className="matrix-number"
              style={{
                left: `${num.x}%`,
                top: `${num.y}%`,
                opacity: num.opacity,
                animationDelay: `${num.id * 0.1}s`
              }}
            >
              {num.value}
            </div>
          ))}
        </div>
        <div className="matrix-connect-screen">
          <div className="connect-icon">⚡</div>
          <h3 className="connect-title">🔄 LOADING MATRIX</h3>
          <p className="connect-desc">Establishing connection to the mainframe...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="numberblast-matrix-arena">
      {/* Matrix Rain Background */}
      <div className="matrix-rain">
        {matrixNumbers.map(num => (
          <div
            key={num.id}
            className="matrix-number"
            style={{
              left: `${num.x}%`,
              top: `${num.y}%`,
              opacity: num.opacity,
              animationDelay: `${num.id * 0.1}s`
            }}
          >
            {num.value}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="matrix-header-bar">
        <button 
          className="matrix-back-btn" 
          onClick={() => {
            playGameSound('digitalClick', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('matrixHover', 0.2)}
        >
          ← DISCONNECT
        </button>
        <div className="matrix-title-section">
          <h2 className="matrix-main-title">🚀 NUMBERBLAST MATRIX</h2>
          <div className="matrix-subtitle">Code Breaker Protocol v{gameStats.effectiveMultiplier}</div>
        </div>
        <div className="matrix-balance-display">
          💎 {formatNumber(gameState.balance)} BLB
          <div className="matrix-player-level">
            RANK {gameState.playerLevel}
          </div>
          <button
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('digitalClick', 0.2);
            }}
            className="matrix-sound-btn"
            title={soundEnabled ? 'Disable Audio' : 'Enable Audio'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          {(isOwner || isAdmin) && (
            <button
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('digitalClick', 0.3);
              }}
              onMouseEnter={() => playGameSound('matrixHover', 0.2)}
              className={`matrix-admin-toggle-btn ${showAdminPanel ? 'active' : ''}`}
              title="Admin Terminal"
            >
              ⚙️
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="matrix-message matrix-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="matrix-message matrix-success-msg">
          {successMessage}
        </div>
      )}

      <div className="matrix-game-layout">

        {/* Admin Panel */}
        {showAdminPanel && (isOwner || isAdmin) && (
          <div className="matrix-admin-panel">
            <div className="matrix-admin-header">
              <h3>🛡️ ADMIN TERMINAL</h3>
              <div className="admin-role-badge">
                {isOwner ? '👑 ROOT' : '⚡ ADMIN'}
              </div>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="admin-close-btn"
                style={{
                  background: 'rgba(255, 0, 128, 0.3)',
                  border: '2px solid #ff0080',
                  borderRadius: '8px',
                  color: '#ff6b9d',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem'
                }}
              >
                ✕
              </button>
            </div>

            <div className="admin-panel-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '2rem' }}>
              {/* System Status */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(0, 255, 65, 0.1) 0%, rgba(0, 212, 255, 0.05) 100%)',
                border: '3px solid rgba(0, 255, 65, 0.3)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#00ff41', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🔐 System Status
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{ color: 'rgba(0, 255, 65, 0.7)', fontWeight: '600' }}>Status:</span>
                  <span style={{
                    color: adminState.isPaused ? '#ff0080' : '#00ff41',
                    fontWeight: '800',
                    textShadow: '0 0 10px currentColor'
                  }}>
                    {adminState.isPaused ? '⏸️ OFFLINE' : '▶️ ONLINE'}
                  </span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('digitalClick', 0.3);
                      
                      if (contracts.numberBlast) {
                        const tx = adminState.isPaused ? 
                          await contracts.numberBlast.unpauseGame() : 
                          await contracts.numberBlast.pauseGame();
                        
                        setSuccessMessage(`System ${adminState.isPaused ? 'activated' : 'deactivated'}...`);
                        await tx.wait();
                        
                        await loadAdminData();
                        setSuccessMessage(`✅ System ${adminState.isPaused ? 'activated' : 'deactivated'} successfully!`);
                      } else {
                        setSuccessMessage(`✅ System ${adminState.isPaused ? 'activated' : 'deactivated'}! (Demo)`);
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Error toggling system:', error);
                      setErrorMessage(`Failed to ${adminState.isPaused ? 'activate' : 'deactivate'} system: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: adminState.isPaused ? 
                      'linear-gradient(135deg, #00ff41 0%, #00d4ff 100%)' : 
                      'linear-gradient(135deg, #ff0080 0%, #ff6b00 100%)',
                    border: `3px solid ${adminState.isPaused ? '#00ff41' : '#ff0080'}`,
                    borderRadius: '10px',
                    color: '#000',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: `0 0 15px rgba(${adminState.isPaused ? '0, 255, 65' : '255, 0, 128'}, 0.4)`
                  }}
                >
                  {adminState.isPaused ? '▶️ ACTIVATE' : '⏸️ DEACTIVATE'}
                </button>
              </div>

              {/* Treasury Balance */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 107, 0, 0.05) 100%)',
                border: '3px solid rgba(255, 215, 0, 0.3)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#ffd700', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  💰 System Treasury
                </h4>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ color: 'rgba(255, 215, 0, 0.7)', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Current Balance:
                  </div>
                  <div style={{ 
                    color: '#ffd700', 
                    fontSize: '1.5rem', 
                    fontWeight: '800',
                    textShadow: '0 0 15px currentColor'
                  }}>
                    ⚡ {formatNumber(adminState.houseBalance)} BLB
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('digitalClick', 0.3);
                      
                      if (contracts.numberBlast && contracts.numberBlast.emergencyWithdrawAll) {
                        const tx = await contracts.numberBlast.emergencyWithdrawAll();
                        setSuccessMessage('Extracting all treasury funds...');
                        await tx.wait();
                        
                        await Promise.all([loadAdminData(), loadBalance()]);
                        setSuccessMessage('✅ Emergency extraction completed!');
                      } else {
                        setSuccessMessage('✅ Emergency extraction completed! (Demo)');
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Emergency withdraw error:', error);
                      setErrorMessage(`Emergency extraction failed: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: 'linear-gradient(135deg, #ff0080 0%, #ff6b00 100%)',
                    border: '3px solid #ff0080',
                    borderRadius: '10px',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: '0 0 15px rgba(255, 0, 128, 0.4)'
                  }}
                >
                  🚨 EMERGENCY EXTRACTION
                </button>
              </div>

              {/* Fund Treasury */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 255, 65, 0.05) 100%)',
                border: '3px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#00d4ff', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  💳 Fund Treasury
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.fundAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, fundAmount: e.target.value }))}
                    placeholder="Amount to fund treasury"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '2px solid rgba(0, 212, 255, 0.5)',
                      borderRadius: '10px',
                      color: '#00d4ff',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.fundAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid fund amount');
                          return;
                        }
                        
                        playGameSound('digitalClick', 0.3);
                        
                        if (contracts.numberBlast && contracts.blbToken) {
                          const amountWei = ethers.utils.parseEther(amount);
                          
                          const allowance = await contracts.blbToken.allowance(walletData.account, contracts.numberBlast.address);
                          if (allowance.lt(amountWei)) {
                            const approveTx = await contracts.blbToken.approve(contracts.numberBlast.address, ethers.constants.MaxUint256);
                            await approveTx.wait();
                          }
                          
                          const tx = await contracts.numberBlast.fundHouse(amountWei);
                          setSuccessMessage('Funding treasury...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Treasury funded with ${formatNumber(amount)} BLB!`);
                        } else {
                          setSuccessMessage(`✅ Treasury funded with ${formatNumber(amount)} BLB! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, fundAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error funding treasury:', error);
                        setErrorMessage(`Failed to fund treasury: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.fundAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #00ff41 0%, #00d4ff 100%)',
                      border: '3px solid #00ff41',
                      borderRadius: '10px',
                      color: '#000',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.fundAmount ? 1 : 0.6,
                      boxShadow: adminState.fundAmount ? '0 0 15px rgba(0, 255, 65, 0.4)' : 'none'
                    }}
                  >
                    💎 FUND TREASURY
                  </button>
                </div>
              </div>

              {/* Withdraw Treasury */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 0, 128, 0.05) 100%)',
                border: '3px solid rgba(255, 107, 0, 0.3)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#ff6b00', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  💸 Withdraw Treasury
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.withdrawAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, withdrawAmount: e.target.value }))}
                    placeholder="Amount to withdraw"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '2px solid rgba(255, 107, 0, 0.5)',
                      borderRadius: '10px',
                      color: '#ff6b00',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.withdrawAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid withdraw amount');
                          return;
                        }
                        
                        playGameSound('digitalClick', 0.3);
                        
                        if (contracts.numberBlast) {
                          const amountWei = ethers.utils.parseEther(amount);
                          const tx = await contracts.numberBlast.withdrawHouse(amountWei);
                          setSuccessMessage('Withdrawing from treasury...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from treasury!`);
                        } else {
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from treasury! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, withdrawAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error withdrawing from treasury:', error);
                        setErrorMessage(`Failed to withdraw: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.withdrawAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #ff6b00 0%, #ff0080 100%)',
                      border: '3px solid #ff6b00',
                      borderRadius: '10px',
                      color: '#fff',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.withdrawAmount ? 1 : 0.6,
                      boxShadow: adminState.withdrawAmount ? '0 0 15px rgba(255, 107, 0, 0.4)' : 'none'
                    }}
                  >
                    💸 WITHDRAW
                  </button>
                </div>
              </div>

              {/* System Limits */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(255, 0, 128, 0.1) 0%, rgba(0, 255, 65, 0.05) 100%)',
                border: '3px solid rgba(255, 0, 128, 0.3)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#ff0080', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🎯 System Limits
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(255, 0, 128, 0.7)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MIN BET
                      </label>
                      <input
                        type="number"
                        value={adminState.newMinBet || adminState.minBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMinBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(255, 0, 128, 0.5)',
                          borderRadius: '8px',
                          color: '#ff0080',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(255, 0, 128, 0.7)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MAX BET
                      </label>
                      <input
                        type="number"
                        value={adminState.newMaxBet || adminState.maxBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMaxBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(255, 0, 128, 0.5)',
                          borderRadius: '8px',
                          color: '#ff0080',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const minBet = parseFloat(adminState.newMinBet || adminState.minBet);
                        const maxBet = parseFloat(adminState.newMaxBet || adminState.maxBet);

                        if (minBet <= 0 || maxBet <= 0 || minBet >= maxBet) {
                          setErrorMessage('Invalid bet limits');
                          return;
                        }

                        playGameSound('digitalClick', 0.3);
                        
                        if (contracts.numberBlast) {
                          const minBetWei = ethers.utils.parseEther(minBet.toString());
                          const maxBetWei = ethers.utils.parseEther(maxBet.toString());
                          
                          const tx = await contracts.numberBlast.setBetLimits(minBetWei, maxBetWei);
                          setSuccessMessage('Updating system limits...');
                          await tx.wait();
                          
                          await loadAdminData();
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB`);
                        } else {
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB (Demo)`);
                        }
                        
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error setting bet limits:', error);
                        setErrorMessage(`Failed to set limits: ${error.reason || error.message}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #ff0080 0%, #00ff41 100%)',
                      border: '3px solid #ff0080',
                      borderRadius: '10px',
                      color: '#fff',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      boxShadow: '0 0 15px rgba(255, 0, 128, 0.4)'
                    }}
                  >
                    🎯 SET LIMITS
                  </button>
                </div>
              </div>

              {/* Win Chances Configuration */}
              <div className="admin-section" style={{
                background: 'linear-gradient(135deg, rgba(0, 255, 65, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)',
                border: '3px solid rgba(0, 255, 65, 0.3)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="admin-section-title" style={{ color: '#00ff41', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🎲 Matrix Configuration
                </h4>
                <div style={{ fontSize: '0.85rem', color: 'rgba(0, 255, 65, 0.6)', marginBottom: '1rem', fontStyle: 'italic', textAlign: 'center' }}>
                  Configure win chances and payouts for the matrix system
                </div>
                
                {/* Chance Configuration */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ color: '#00ff41', fontWeight: '700', marginBottom: '0.8rem', textAlign: 'center' }}>
                    WIN CHANCES (%)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(0, 255, 65, 0.7)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        HOUSE WIN (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newWinChances?.houseWin || '4.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newWinChances: { ...prev.newWinChances, houseWin: e.target.value }
                        }))}
                        min="0"
                        max="50"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(0, 255, 65, 0.5)',
                          borderRadius: '8px',
                          color: '#00ff41',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(0, 255, 65, 0.7)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        ODD/EVEN (% each)
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="number"
                          placeholder="ODD %"
                          value={adminState.newWinChances?.odd || '15.0'}
                          onChange={(e) => setAdminState(prev => ({ 
                            ...prev, 
                            newWinChances: { ...prev.newWinChances, odd: e.target.value }
                          }))}
                          min="0"
                          max="30"
                          step="0.1"
                          style={{
                            flex: 1,
                            padding: '0.6rem',
                            background: 'rgba(0, 0, 0, 0.7)',
                            border: '2px solid rgba(0, 255, 65, 0.5)',
                            borderRadius: '8px',
                            color: '#00ff41',
                            fontSize: '0.9rem',
                            fontWeight: '700',
                            boxSizing: 'border-box'
                          }}
                        />
                        <input
                          type="number"
                          placeholder="EVEN %"
                          value={adminState.newWinChances?.even || '15.0'}
                          onChange={(e) => setAdminState(prev => ({ 
                            ...prev, 
                            newWinChances: { ...prev.newWinChances, even: e.target.value }
                          }))}
                          min="0"
                          max="30"
                          step="0.1"
                          style={{
                            flex: 1,
                            padding: '0.6rem',
                            background: 'rgba(0, 0, 0, 0.7)',
                            border: '2px solid rgba(0, 255, 65, 0.5)',
                            borderRadius: '8px',
                            color: '#00ff41',
                            fontSize: '0.9rem',
                            fontWeight: '700',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ color: 'rgba(0, 255, 65, 0.7)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        HIGH/LOW (% each)
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          type="number"
                          placeholder="HIGH %"
                          value={adminState.newWinChances?.high || '15.0'}
                          onChange={(e) => setAdminState(prev => ({ 
                            ...prev, 
                            newWinChances: { ...prev.newWinChances, high: e.target.value }
                          }))}
                          min="0"
                          max="30"
                          step="0.1"
                          style={{
                            flex: 1,
                            padding: '0.6rem',
                            background: 'rgba(0, 0, 0, 0.7)',
                            border: '2px solid rgba(0, 255, 65, 0.5)',
                            borderRadius: '8px',
                            color: '#00ff41',
                            fontSize: '0.9rem',
                            fontWeight: '700',
                            boxSizing: 'border-box'
                          }}
                        />
                        <input
                          type="number"
                          placeholder="LOW %"
                          value={adminState.newWinChances?.low || '15.0'}
                          onChange={(e) => setAdminState(prev => ({ 
                            ...prev, 
                            newWinChances: { ...prev.newWinChances, low: e.target.value }
                          }))}
                          min="0"
                          max="30"
                          step="0.1"
                          style={{
                            flex: 1,
                            padding: '0.6rem',
                            background: 'rgba(0, 0, 0, 0.7)',
                            border: '2px solid rgba(0, 255, 65, 0.5)',
                            borderRadius: '8px',
                            color: '#00ff41',
                            fontSize: '0.9rem',
                            fontWeight: '700',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ color: 'rgba(0, 255, 65, 0.7)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        EXACT NUMBER (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newWinChances?.exact || '0.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newWinChances: { ...prev.newWinChances, exact: e.target.value }
                        }))}
                        min="0"
                        max="5"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(0, 255, 65, 0.5)',
                          borderRadius: '8px',
                          color: '#00ff41',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Status Info */}
                <div style={{ marginBottom: '1rem', padding: '1rem', background: 'rgba(0, 255, 65, 0.05)', borderRadius: '10px', border: '2px solid rgba(0, 255, 65, 0.2)' }}>
                  <div style={{ color: '#00ff41', fontWeight: '600', marginBottom: '0.5rem', textAlign: 'center' }}>
                    <strong>Total:</strong> {(
                      parseFloat(adminState.newWinChances?.houseWin || 4) + 
                      parseFloat(adminState.newWinChances?.odd || 15) +
                      parseFloat(adminState.newWinChances?.even || 15) +
                      parseFloat(adminState.newWinChances?.high || 15) +
                      parseFloat(adminState.newWinChances?.low || 15) +
                      parseFloat(adminState.newWinChances?.exact || 0)
                    ).toFixed(1)}%
                  </div>
                  <div style={{ color: '#00ff41', fontWeight: '600', textAlign: 'center' }}>
                    <strong>House Edge:</strong> <span style={{ color: parseFloat(adminState.newWinChances?.houseWin || 4) > 10 ? '#ff0080' : '#00ff41' }}>
                      {parseFloat(adminState.newWinChances?.houseWin || 4).toFixed(1)}%
                    </span>
                  </div>
                  {Math.abs((
                    parseFloat(adminState.newWinChances?.houseWin || 4) + 
                    parseFloat(adminState.newWinChances?.odd || 15) +
                    parseFloat(adminState.newWinChances?.even || 15) +
                    parseFloat(adminState.newWinChances?.high || 15) +
                    parseFloat(adminState.newWinChances?.low || 15) +
                    parseFloat(adminState.newWinChances?.exact || 0)
                  ) - 100) > 0.1 && (
                    <div style={{ color: '#ff0080', fontWeight: '800', marginTop: '0.5rem', textAlign: 'center' }}>
                      ⚠️ Must equal 100%
                    </div>
                  )}
                </div>

                {/* Update Button */}
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      const totalChance = 
                        parseFloat(adminState.newWinChances?.houseWin || 4) + 
                        parseFloat(adminState.newWinChances?.odd || 15) +
                        parseFloat(adminState.newWinChances?.even || 15) +
                        parseFloat(adminState.newWinChances?.high || 15) +
                        parseFloat(adminState.newWinChances?.low || 15) +
                        parseFloat(adminState.newWinChances?.exact || 0);
                      
                      if (Math.abs(totalChance - 100) > 0.1) {
                        setErrorMessage('Total chances must equal 100%');
                        return;
                      }

                      playGameSound('digitalClick', 0.3);
                      setSuccessMessage('✅ Matrix configuration updated successfully! (Demo)');
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Error updating matrix config:', error);
                      setErrorMessage(`Failed to update config: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: 'linear-gradient(135deg, #00ff41 0%, #00d4ff 100%)',
                    border: '3px solid #00ff41',
                    borderRadius: '10px',
                    color: '#000',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: '0 0 15px rgba(0, 255, 65, 0.4)'
                  }}
                >
                  🎲 UPDATE MATRIX
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Game Zone */}
        <div className="matrix-game-zone">
          {/* Breach Popup */}
          {gameState.showBreachPopup && (
            <div className="matrix-breach-popup-overlay">
              <div className="matrix-breach-popup">
                <div className="breach-header">
                  <h2>🚀 INITIATING CODE BREACH!</h2>
                  <div className="breach-info">
                    <span>Target: {BET_TYPES[gameState.selectedBetType]?.name}</span>
                    <span>Stake: {formatNumber(gameState.betAmount)} BLB</span>
                    {gameState.selectedBetType === 4 && (
                      <span>Exact Target: {gameState.exactNumber}</span>
                    )}
                  </div>
                </div>

                <div className="breach-display-zone">
                  <div className="breach-target-display">
                    <div className="target-icon">
                      {BET_TYPES[gameState.selectedBetType]?.icon}
                    </div>
                    <div className="target-name">
                      {BET_TYPES[gameState.selectedBetType]?.name}
                    </div>
                  </div>

                  <div className="breach-progress">
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                    <div className="progress-text">BREACHING MATRIX...</div>
                  </div>
                </div>

                {/* Result Display in Popup */}
                {animationState === 'result' && breachResult && (
                  <div className="breach-result-display">
                    <div className={`breach-result-content ${breachResult.won ? 'breach-success' : 'breach-failure'}`}>
                      <div className="breach-result-icon">
                        {breachResult.won ? '🎊' : '⚡'}
                      </div>
                      <h3 className="breach-result-title">
                        {breachResult.won ? '🚀 BREACH SUCCESSFUL!' : '🛡️ SYSTEM DEFENDED'}
                      </h3>
                      <div className="breach-result-amount">
                        {breachResult.won ? `+${formatNumber(breachResult.netPayout)}` : `-${formatNumber(breachResult.betAmount)}`} BLB
                      </div>
                      <div className="breach-result-desc">
                        Drawn Number: {breachResult.drawnNumber}
                      </div>
                      <div className="breach-result-desc">
                        Your Choice: {getBetTypeName(breachResult.betType)}
                        {breachResult.betType === 4 && ` (${breachResult.exactNumber})`}
                      </div>
                      {breachResult.won && gameState.streak > 1 && (
                        <div className="breach-streak-display">
                          🔥 {gameState.streak}X BREACH STREAK! 🔥
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Breaching indicator */}
                {animationState === 'breaching' && (
                  <div className="breaching-indicator">
                    <div className="breach-text">⚡ BREAKING THE CODE...</div>
                    <div className="breach-subtext">Decrypting system matrix...</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Display Area */}
          <div className="matrix-display-zone">
            <div className="main-display-screen">
              <div className="display-header">
                <span>TARGET SELECTION</span>
                <span className="display-status">READY</span>
              </div>
              
              {gameState.selectedBetType !== null && (
                <div className="selected-target-display">
                  <div className="target-info">
                    <div className="target-visual">
                      <span className="target-emoji">{BET_TYPES[gameState.selectedBetType]?.icon}</span>
                      <div className="target-name">{BET_TYPES[gameState.selectedBetType]?.name}</div>
                      <div className="target-desc">{BET_TYPES[gameState.selectedBetType]?.description}</div>
                    </div>
                    <div className="payout-info">
                      <div className="potential-win">
                        POTENTIAL: {formatNumber((parseFloat(gameState.betAmount) * parseFloat(getMultiplier())).toString())} BLB
                      </div>
                      <div className="multiplier">
                        {getMultiplier()}x MULTIPLIER
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Controls */}
          <div className="matrix-controls">
            {/* Stake Input */}
            <div className="matrix-control-panel">
              <label className="matrix-control-label">💰 BREACH AMOUNT</label>
              <div>
                <input
                  className="matrix-stake-input"
                  type="number"
                  value={gameState.betAmount}
                  onChange={(e) => setGameState(prev => ({ ...prev, betAmount: e.target.value }))}
                  disabled={gameState.isPlaying}
                  min="1"
                  max={gameState.balance}
                  step="1"
                  onFocus={() => playGameSound('digitalClick', 0.1)}
                />
                <div className="matrix-preset-buttons">
                  {['1', '10', '100'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        playGameSound('digitalClick', 0.2);
                        setGameState(prev => ({ ...prev, betAmount: amount }));
                      }}
                      onMouseEnter={() => playGameSound('matrixHover', 0.1)}
                      disabled={gameState.isPlaying}
                      className="matrix-preset-btn"
                    >
                      {amount}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      playGameSound('digitalClick', 0.2);
                      setGameState(prev => ({ ...prev, betAmount: Math.floor(parseFloat(prev.balance)).toString() }));
                    }}
                    onMouseEnter={() => playGameSound('matrixHover', 0.1)}
                    disabled={gameState.isPlaying}
                    className="matrix-preset-btn max-btn"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            {/* Bet Type Selection */}
            <div className="matrix-control-panel">
              <label className="matrix-control-label">🎯 BREACH VECTOR</label>
              <div className="matrix-bet-grid">
                {BET_TYPES.map((betType) => (
                  <button
                    key={betType.id}
                    className={`matrix-bet-btn ${gameState.selectedBetType === betType.id ? 'selected' : ''}`}
                    style={{
                      background: gameState.selectedBetType === betType.id 
                        ? `linear-gradient(135deg, ${betType.color}, ${betType.color}cc)` 
                        : `linear-gradient(135deg, ${betType.color}33, ${betType.color}11)`,
                      borderColor: betType.color,
                      boxShadow: gameState.selectedBetType === betType.id 
                        ? `0 0 25px rgba(${betType.glowColor}, 0.6)` 
                        : `0 0 10px rgba(${betType.glowColor}, 0.2)`
                    }}
                    onClick={() => handleBetTypeSelect(betType.id)}
                    onMouseEnter={() => playGameSound('matrixHover', 0.15)}
                    disabled={gameState.isPlaying}
                  >
                    <div className="bet-icon">{betType.icon}</div>
                    <span className="bet-name">{betType.name}</span>
                    <span className="bet-desc">{betType.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Exact Number Input (for EXACT bet type) */}
            {gameState.selectedBetType === 4 && (
              <div className="matrix-control-panel">
                <label className="matrix-control-label">🎯 TARGET NUMBER (1-100)</label>
                <input
                  className="matrix-exact-input"
                  type="number"
                  value={gameState.exactNumber}
                  onChange={(e) => setGameState(prev => ({ ...prev, exactNumber: e.target.value }))}
                  disabled={gameState.isPlaying}
                  min="1"
                  max="100"
                  step="1"
                  onFocus={() => playGameSound('digitalClick', 0.1)}
                />
              </div>
            )}

            {/* Action Button */}
            <div className="matrix-control-panel">
              {needsApproval() ? (
                <button
                  className="matrix-action-btn matrix-approve-btn"
                  onClick={handleApprove}
                  onMouseEnter={() => playGameSound('matrixHover', 0.2)}
                  disabled={gameState.isApproving}
                >
                  {gameState.isApproving ? '⚡ AUTHORIZING...' : '🔐 GRANT ACCESS'}
                </button>
              ) : (
                <button
                  className="matrix-action-btn matrix-breach-btn"
                  onClick={handleBreachCode}
                  onMouseEnter={() => playGameSound('matrixHover', 0.2)}
                  disabled={!canBreachCode()}
                >
                  {gameState.isPlaying ? '⚡ BREACHING...' : '🚀 BREACH CODE'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Sidebar */}
        <div className="matrix-stats-sidebar">
          <div className="matrix-stats-panel">
            <h3 className="matrix-stats-title">🏆 HACKER STATS</h3>
            <div className="matrix-stats-item">
              <span className="matrix-stats-label">Rank</span>
              <span className="matrix-stats-value">{gameState.playerLevel}</span>
            </div>
            <div className="matrix-stats-item">
              <span className="matrix-stats-label">EXP</span>
              <span className="matrix-stats-value">{formatNumber(gameState.experience)}</span>
            </div>
            <div className="matrix-stats-item">
              <span className="matrix-stats-label">Streak</span>
              <span className="matrix-stats-value">{gameState.streak}x 🔥</span>
            </div>
          </div>

          <div className="matrix-stats-panel">
            <h3 className="matrix-stats-title">📊 SYSTEM STATS</h3>
            <div className="matrix-stats-item">
              <span className="matrix-stats-label">Online</span>
              <span className="matrix-stats-value">{gameStats.playersOnline}</span>
            </div>
            <div className="matrix-stats-item">
              <span className="matrix-stats-label">Total Games</span>
              <span className="matrix-stats-value">{formatNumber(gameStats.totalGames)}</span>
            </div>
            <div className="matrix-stats-item">
              <span className="matrix-stats-label">Volume</span>
              <span className="matrix-stats-value">{formatNumber(gameStats.totalBets)}</span>
            </div>
          </div>

          <div className="matrix-stats-panel">
            <h3 className="matrix-stats-title">🎯 BREACH GUIDE</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[
                'Select breach vector',
                'Set your stake amount',
                'Execute breach attempt',
                'Decrypt the matrix code!'
              ].map((step, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    background: 'linear-gradient(45deg, #00ff41, #00d4ff)', 
                    color: '#000', 
                    width: '2rem', 
                    height: '2rem', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontWeight: '800', 
                    fontSize: '0.9rem', 
                    flexShrink: 0,
                    boxShadow: '0 0 8px rgba(0, 255, 65, 0.4)'
                  }}>{index + 1}</div>
                  <div style={{ color: 'rgba(0, 255, 65, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                    {step}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {gameHistory.length > 0 && (
            <div className="matrix-stats-panel">
              <h3 className="matrix-stats-title">📜 RECENT BREACHES</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '200px', overflowY: 'auto' }}>
                {gameHistory.map((game, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.8rem',
                    background: 'rgba(0, 255, 65, 0.03)',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${game.won ? '#00ff41' : '#ff0080'}`,
                  }}>
                    <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                      {BET_TYPES[game.betType]?.icon || '🎯'}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ color: 'rgba(0, 255, 65, 0.7)', fontSize: '0.8rem', fontWeight: '600' }}>
                          {BET_TYPES[game.betType]?.name || 'UNKNOWN'}
                        </div>
                        <div style={{ 
                          color: game.won ? '#00ff41' : '#ff0080', 
                          fontWeight: '800', 
                          fontSize: '0.9rem'
                        }}>
                          {game.won ? `+${formatNumber(game.netPayout)}` : 'FAIL'}
                        </div>
                      </div>
                      <div style={{ color: 'rgba(0, 255, 65, 0.5)', fontSize: '0.7rem' }}>
                        Drew: {game.drawnNumber} | Bet: {formatNumber(game.betAmount)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NumberBlast;